import { clsx } from 'clsx'
import { Fragment, useState } from 'react'
import JourneyActionItemButton from './journey-action-item-button'

interface ActionItemData {
  id: string
  title: string
  category: string
  issueDescription: string | null
}

interface ProgressStep {
  label: string
  state: 'completed' | 'current' | 'upcoming'
  number: number
}

interface Props {
  actionItems?: ActionItemData[]
  progressTrackerSteps: ProgressStep[]
}

export function JourneyActionItemCallout({ actionItems = [], progressTrackerSteps }: Props) {
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set())

  const visibleCount = actionItems.length - resolvedIds.size

  const handleResolved = (id: string) => {
    setResolvedIds((prev) => new Set([...prev, id]))
  }

  const handleResolveError = (id: string) => {
    setResolvedIds((prev) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  return (
    <div className="bg-white rounded-lg border border-black/10 shadow-sm p-4 flex flex-col gap-6">
      {visibleCount > 0 && (
        <div>
          <span className="inline-flex items-center gap-2 rounded-2xl border border-brick bg-peach-25 px-2 py-1 text-sm font-medium text-charcoal">
            <span className="w-2 h-2 rounded-full bg-brick-light shrink-0" />
            Needs school action
          </span>
        </div>
      )}

      {visibleCount > 0 && (
        <div className="flex flex-col gap-4">
          {actionItems.map((item, index) => (
            <div
              key={item.id}
              id={`action-item-card-${item.id}`}
              className="flex items-center gap-4 rounded-lg border border-sand-dark bg-sand-25 p-4 shadow-sm"
            >
              <div className="flex-1 min-w-0">
                {item.issueDescription &&
                  (actionItems.length > 1 ? (
                    <ol
                      start={index + 1}
                      className="list-decimal ms-[21px] text-sm font-medium text-charcoal-75"
                    >
                      <li>
                        <span className="leading-normal">{item.issueDescription}</span>
                      </li>
                    </ol>
                  ) : (
                    <span className="text-sm font-medium text-charcoal-75 leading-normal">
                      {item.issueDescription}
                    </span>
                  ))}
              </div>
              <div className="shrink-0">
                <JourneyActionItemButton
                  actionItemId={item.id}
                  calloutContainerId={`action-item-card-${item.id}`}
                  source="student_profile"
                  onResolved={() => handleResolved(item.id)}
                  onResolveError={() => handleResolveError(item.id)}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <ProgressTracker steps={progressTrackerSteps} />
    </div>
  )
}

// Build grid columns dynamically: current step gets 28px (bullseye), others get 20px,
// with 1fr connector columns between each step.
function gridCols(steps: ProgressStep[]) {
  return steps.map((s) => `${s.state === 'current' ? 28 : 20}px`).join(' 1fr ')
}

function ProgressTracker({ steps }: { steps: ProgressStep[] }) {
  return (
    <div className="grid" style={{ gridTemplateColumns: gridCols(steps), rowGap: '10px' }}>
      {/* Row 1: icons + connector lines */}
      {steps.map((step, index) => (
        <Fragment key={`icon-row-${step.number}`}>
          <StepIcon step={step} />
          {index < steps.length - 1 && <div className="h-[1.5px] bg-charcoal-25 self-center" />}
        </Fragment>
      ))}

      {/* Row 2: labels + empty connector cells */}
      {steps.map((step, index) => {
        const labelColor = step.state === 'upcoming' ? 'text-charcoal-25' : 'text-forest'
        const justify =
          index === 0
            ? 'justify-start'
            : index === steps.length - 1
              ? 'justify-end'
              : 'justify-center'
        const textAlign =
          index === 0 ? 'text-left' : index === steps.length - 1 ? 'text-right' : 'text-center'

        return (
          <Fragment key={`label-row-${step.number}`}>
            <div className={clsx('flex', justify)}>
              <span
                className={clsx(
                  'text-[12px] font-medium leading-snug w-max',
                  labelColor,
                  textAlign
                )}
              >
                {step.label.split('\n').map((line, i, arr) => (
                  <Fragment key={line}>
                    {line}
                    {i < arr.length - 1 && <br />}
                  </Fragment>
                ))}
              </span>
            </div>
            {index < steps.length - 1 && <div />}
          </Fragment>
        )
      })}
    </div>
  )
}

function StepIcon({ step }: { step: ProgressStep }) {
  if (step.state === 'current') {
    return (
      <div className="relative w-7 h-7 shrink-0 bg-white rounded-full">
        <span className="absolute inset-0 rounded-full bg-forest/20" />
        <span className="absolute inset-0.5 rounded-full bg-forest/30" />
        <span className="absolute inset-1 rounded-full bg-forest" />
        <span className="absolute inset-[11px] rounded-full bg-white" />
      </div>
    )
  }

  if (step.state === 'completed') {
    return (
      <div className="relative flex items-center justify-center w-5 h-5 bg-white rounded-full self-center">
        <i className="fa-sharp fa-solid fa-circle-check text-[20px] text-forest absolute" />
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center w-5 h-5 rounded-full border-[1.5px] border-charcoal-25 bg-white self-center shrink-0">
      <span className="text-[10px] font-medium text-charcoal-25 leading-none">{step.number}</span>
    </div>
  )
}

export default JourneyActionItemCallout
